<template>
  <div v-if="type === 'screen'" class="card">
    <div class="card-header">
      <p class="text-subtitle mb-0">{{ user_name }}</p>
    </div>
    <div
      class="card-body overflow-scroll py-0"
      id="chat_card_body"
      style="height: calc(100vh - 442px)"
    >
      <LoadingContainer v-if="loadingChat" />
      <ChatRoomContent
        v-else
        :formattedDate="formatted_date"
        :sectionItems="sectionItems"
        :defaultMessage="defaultMessage"
        :sessionId="session?.id"
        :editingMessageId="editingMessageId"
        :editMessageText="editMessageText"
        :canEditMessage="canEditMessage"
        @startEdit="startEdit"
        @cancelEdit="cancelEdit"
        @submitEdit="submitEdit"
        @update:editMessageText="editMessageText = $event"
      />
    </div>
    <div class="card-footer">
      <ChatRoomInput @handleSend="handleSend" />
    </div>
  </div>

  <template v-if="type === 'modal'">
    <div class="modal-body py-0 d-flex flex-column" id="chat_modal_body">
      <LoadingContainer v-if="loadingChat" />
      <ChatRoomContent
        v-else
        :formattedDate="formatted_date"
        :sectionItems="sectionItems"
        :defaultMessage="defaultMessage"
        :sessionId="session?.id"
        :editingMessageId="editingMessageId"
        :editMessageText="editMessageText"
        :canEditMessage="canEditMessage"
        @startEdit="startEdit"
        @cancelEdit="cancelEdit"
        @submitEdit="submitEdit"
        @update:editMessageText="editMessageText = $event"
      />
    </div>
    <div class="modal-footer">
      <ChatRoomInput @handleSend="handleSend" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import moment from "moment";
import { useChatRoomConfigStore } from "@/store/useChatRoomConfigStore";
import { useSessionStore } from "@/store/useSessionStore";
import { chatDefaultMessage } from "@/constants";
import ChatRoomContent from "./ChatRoomContent.vue";
import ChatRoomInput from "./ChatRoomInput.vue";
import { useChatRoom, useSendChatMessage, useEditChatMessage } from "@/composables/useChat";

defineProps<{ type?: string }>();

const store = useChatRoomConfigStore();
const sessionStore = useSessionStore();
const session = computed(() => sessionStore.session);
const chatId = computed(() => store.config?.id as number | undefined);
const chatDate = computed(() => store.config?.date as string | undefined);
const user_name = computed(() => store.config?.user_name as string);

const defaultMessage = chatDefaultMessage;

const { sectionItems, loadingChat, formatted_date, invalidateMessages } = useChatRoom(chatId, chatDate);

const { mutate: sendMessageMutation } = useSendChatMessage();
const { mutateAsync: editMessageMutation } = useEditChatMessage();

const editingMessageId = ref<number | null>(null);
const editMessageText = ref("");

function handleSend(msg: string) {
  if (!chatId.value) return;
  sendMessageMutation(
    { appointment_chat_id: chatId.value, message: msg },
    { onSuccess: () => invalidateMessages() },
  );
}

function canEditMessage(item: any) {
  const created = moment(item.created_at || item.date);
  return moment().diff(created, "minutes") < 15;
}

function startEdit(item: any) {
  editingMessageId.value = item.id;
  editMessageText.value = item.message;
}

function cancelEdit() {
  editingMessageId.value = null;
  editMessageText.value = "";
}

async function submitEdit(item: any) {
  const text = editMessageText.value.trim();
  if (!text) return;
  try {
    await editMessageMutation({ id: item.id, message: text });
    invalidateMessages();
    cancelEdit();
  } catch (error) {
    console.error("submitEdit", error);
  }
}
</script>
